<script setup lang="ts">
import type { EventResponse, RegistrationTypeResponse } from '@@/types/api';
import { formatCurrency } from '~/composables/format-currency';
import { formatEventDateTime } from '~/composables/format-datetime';
import { fundingDisplay, isTicketSalesClosed } from '~/utils/event-policy';

const props = defineProps<{
  event: EventResponse;
}>();

const fundingDescriptor = computed(() => fundingDisplay(props.event));

const POST_CONFIRMATION = new Set(['Confirmed', 'In Progress', 'Completed']);
const isPostConfirmation = computed(() =>
  POST_CONFIRMATION.has(props.event.user_facing_status),
);

const registrationTypes = computed(() => props.event.registration_types ?? []);
const isMultiType = computed(() => registrationTypes.value.length > 1);

// For single-type: use first registration type's pricing, fallback to event.pricing
const defaultPricing = computed(() => {
  const rt = registrationTypes.value[0];
  return rt?.pricing ?? props.event.pricing;
});

const isFreeToAttend = computed(
  () =>
    defaultPricing.value.commit.ticket_price === 0 &&
    !defaultPricing.value.commit.accommodation_price,
);

const ticketSalesCutoff = computed(() => {
  const dt = props.event.effective_ticket_sales_close_datetime;
  if (!dt || !props.event.timezone) return null;
  return formatEventDateTime(dt, props.event.timezone).formatted;
});

const salesClosed = computed(() => isTicketSalesClosed(props.event));

// All-types-sold-out check
const allSoldOut = computed(() => {
  if (!registrationTypes.value.length) return props.event.is_sold_out ?? false;
  return registrationTypes.value.every((rt) => rt.is_sold_out);
});

function typeSavingsText(rt: RegistrationTypeResponse): string | null {
  if (!rt.pricing.has_dual_pricing || !rt.pricing.savings) return null;
  return `Save ${formatCurrency(rt.pricing.savings)} by committing early`;
}
</script>

<template>
  <v-card variant="outlined" rounded="lg" class="event-pricing-card">
    <v-card-text>
      <div class="d-flex align-center mb-4">
        <EventsEventStatusBadge :status="event.user_facing_status" />
        <v-chip v-if="allSoldOut" color="error" size="small" label class="ml-2">
          Sold out
        </v-chip>
      </div>

      <!-- Multi-type: per-type pricing cards -->
      <template v-if="isMultiType">
        <div
          v-for="(rt, i) in registrationTypes"
          :key="rt.id"
          :class="{ 'mt-4': i > 0 }"
        >
          <v-card variant="tonal" rounded="lg" class="pa-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-subtitle-2 font-weight-bold">
                {{ rt.name }}
              </span>
              <v-chip v-if="rt.is_sold_out" color="error" size="x-small" label>
                Sold out
              </v-chip>
              <span
                v-else-if="rt.registrations_remaining != null"
                class="text-caption text-medium-emphasis"
              >
                {{ rt.registrations_remaining }} spots left
              </span>
            </div>
            <div
              v-if="rt.description"
              class="text-body-2 text-medium-emphasis mb-2"
            >
              {{ rt.description }}
            </div>

            <!-- Post-confirmation: purchase only -->
            <template v-if="isPostConfirmation">
              <EventsPriceBlock
                label="Ticket price"
                :pricing="rt.pricing.purchase"
              />
            </template>

            <!-- Default: commit + purchase pricing -->
            <template v-else>
              <EventsPriceBlock
                label="Commit price"
                :pricing="rt.pricing.commit"
              />

              <EventsPriceBlock
                v-if="rt.pricing.has_dual_pricing"
                label="Purchase price"
                :pricing="rt.pricing.purchase"
                class="mt-2"
              />

              <v-chip
                v-if="typeSavingsText(rt)"
                color="tertiary"
                size="x-small"
                label
                class="mt-2"
              >
                {{ typeSavingsText(rt) }}
              </v-chip>
            </template>
          </v-card>
        </div>
      </template>

      <!-- Single-type pricing -->
      <template v-else>
        <!-- Free-to-attend -->
        <template v-if="isFreeToAttend">
          <EventsPriceBlock
            label="Free to attend"
            :pricing="defaultPricing.commit"
            class="mb-3"
          />
        </template>

        <!-- Post-confirmation: purchase pricing only -->
        <template v-else-if="isPostConfirmation">
          <EventsPriceBlock
            label="Ticket price"
            :pricing="defaultPricing.purchase"
            class="mb-3"
          />
        </template>

        <!-- Default: fundraising / pre-fundraising pricing -->
        <template v-else>
          <EventsPriceBlock
            label="Commit price"
            :pricing="defaultPricing.commit"
            class="mb-3"
          />

          <EventsPriceBlock
            v-if="defaultPricing.has_dual_pricing"
            label="Purchase price"
            :pricing="defaultPricing.purchase"
            class="mb-3"
          />

          <v-chip
            v-if="defaultPricing.has_dual_pricing && defaultPricing.savings"
            color="tertiary"
            size="small"
            label
            class="mb-3"
          >
            Save {{ formatCurrency(defaultPricing.savings) }} by committing
            early
          </v-chip>
        </template>

        <!-- Single-type capacity -->
        <div
          v-if="registrationTypes[0]?.registrations_remaining != null"
          class="text-body-2 text-medium-emphasis mb-1"
        >
          <v-icon size="small" class="mr-1">mdi-ticket</v-icon>
          {{ registrationTypes[0]?.registrations_remaining }} spots remaining
        </div>
      </template>

      <v-divider class="mb-3" />

      <!-- Threshold & Capacity (event-level) -->
      <div class="text-body-2 text-medium-emphasis mb-1">
        <template v-if="fundingDescriptor.isGoalMet">
          <v-icon size="small" color="success" class="mr-1"
            >mdi-check-circle</v-icon
          >
          <template v-if="fundingDescriptor.thresholdType === 'cash'">
            Minimum {{ formatCurrency(event.cash_threshold ?? 0) }} revenue —
            goal reached
          </template>
          <template v-else-if="fundingDescriptor.thresholdType === 'attendees'">
            Minimum {{ event.ticket_threshold }}
            {{ event.ticket_threshold === 1 ? 'attendee' : 'attendees' }} — goal
            reached
          </template>
          <template v-else>
            Minimum {{ event.ticket_threshold }}
            {{ event.ticket_threshold === 1 ? 'backer' : 'backers' }} — goal
            reached
          </template>
        </template>
        <template v-else>
          <template v-if="fundingDescriptor.thresholdType === 'cash'">
            <v-icon size="small" class="mr-1">mdi-cash</v-icon>
            Minimum {{ formatCurrency(event.cash_threshold ?? 0) }} revenue
            needed
          </template>
          <template v-else-if="fundingDescriptor.thresholdType === 'attendees'">
            <v-icon size="small" class="mr-1">mdi-account-group</v-icon>
            Minimum {{ event.ticket_threshold }}
            {{ event.ticket_threshold === 1 ? 'attendee' : 'attendees' }} needed
          </template>
          <template v-else>
            <v-icon size="small" class="mr-1">mdi-account-group</v-icon>
            Minimum {{ event.ticket_threshold }}
            {{ event.ticket_threshold === 1 ? 'backer' : 'backers' }} needed
          </template>
        </template>
      </div>
      <div
        v-if="isPostConfirmation && ticketSalesCutoff"
        class="text-body-2 text-medium-emphasis mb-1"
      >
        <v-icon size="small" class="mr-1">mdi-calendar-clock</v-icon>
        <template v-if="salesClosed"
          >Ticket sales ended {{ ticketSalesCutoff }}</template
        >
        <template v-else>Sales end {{ ticketSalesCutoff }}</template>
      </div>

      <v-divider class="my-3" />

      <EventsEventFundingModule :funding="fundingDescriptor" />
    </v-card-text>
  </v-card>
</template>

<style scoped>
.event-pricing-card {
  /* No sticky — multi-type cards can be taller than the viewport,
     causing overlap with sibling action buttons in the sidebar. */
}
</style>
